import { useState, useRef, useEffect } from 'react'
import {
  Copy,
  CheckCircle,
  FileText,
  ChevronDown,
  Type,
  Code,
  FileCode,
  BookOpen,
  Printer,
  Pencil,
  Save,
  X,
} from 'lucide-react'
import {
  cleanAIContent,
  markdownToPlainText,
  markdownToHtml,
  countWords,
  formatSourcesSection,
  type CopySource,
} from '../../lib/content-utils'

interface ContentToolbarProps {
  body: string
  sources?: CopySource[]
  wordCount: number | null
  currentVersion: number | null
  readingMode?: boolean
  onToggleReadingMode?: () => void
  isEditMode?: boolean
  canEdit?: boolean
  onEnterEditMode?: () => void
  onExitEditMode?: () => void
  onSaveVersion?: () => void
  isSavingVersion?: boolean
}

type CopyFormat = 'markdown' | 'plain' | 'html'

export function ContentToolbar({
  body,
  sources,
  wordCount,
  currentVersion,
  readingMode = false,
  onToggleReadingMode,
  isEditMode = false,
  canEdit = false,
  onEnterEditMode,
  onExitEditMode,
  onSaveVersion,
  isSavingVersion = false,
}: ContentToolbarProps) {
  const [copied, setCopied] = useState(false)
  const [showCopyMenu, setShowCopyMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowCopyMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const words = wordCount ?? countWords(body)
  const readingMinutes = words > 0 ? Math.max(1, Math.ceil(words / 200)) : 0

  const copyContent = async (format: CopyFormat) => {
    const cleaned = cleanAIContent(body)
    let text: string

    switch (format) {
      case 'plain':
        text = markdownToPlainText(cleaned) + formatSourcesSection(sources, 'plain')
        break
      case 'html':
        text = markdownToHtml(cleaned) + formatSourcesSection(sources, 'html')
        break
      case 'markdown':
      default:
        text = cleaned + formatSourcesSection(sources, 'markdown')
        break
    }

    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setShowCopyMenu(false)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="flex items-center justify-between py-2 px-4 bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
      <div className="flex items-center gap-4 text-sm text-slate-500">
        {words > 0 && (
          <span className="flex items-center gap-1.5">
            <FileText className="w-4 h-4" />
            {words} palabras
          </span>
        )}
        {readingMinutes > 0 && (
          <span className="flex items-center gap-1.5">
            {readingMinutes} min de lectura
          </span>
        )}
        {currentVersion && currentVersion > 1 && (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-200 rounded text-xs font-medium">
            v{currentVersion}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {isEditMode ? (
          <>
            <button
              type="button"
              onClick={onExitEditMode}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
              title="Cancelar edicion"
            >
              <X className="w-4 h-4" />
              Cancelar
            </button>
            <button
              type="button"
              onClick={onSaveVersion}
              disabled={isSavingVersion}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-all"
              title="Guardar como nueva version"
            >
              <Save className="w-4 h-4" />
              {isSavingVersion ? 'Guardando...' : 'Guardar version'}
            </button>
          </>
        ) : (
          <>
            {canEdit && onEnterEditMode && (
              <button
                type="button"
                onClick={onEnterEditMode}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
                title="Modo edicion"
              >
                <Pencil className="w-4 h-4" />
                Editar
              </button>
            )}
        {onToggleReadingMode && (
          <button
            onClick={onToggleReadingMode}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              readingMode
                ? 'bg-slate-200 text-slate-800'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
            title={readingMode ? 'Salir del modo lectura' : 'Modo lectura'}
          >
            <BookOpen className="w-4 h-4" />
            Modo lectura
          </button>
        )}
        <button
          onClick={() => window.print()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
          title="Imprimir"
        >
          <Printer className="w-4 h-4" />
          Imprimir
        </button>
        {/* Copy button with dropdown */}
        <div className="relative" ref={menuRef}>
          <div className="flex items-center">
            <button
              onClick={() => copyContent('markdown')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg text-sm font-medium transition-all ${
                copied
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {copied ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copiar
                </>
              )}
            </button>
            <button
              onClick={() => setShowCopyMenu(!showCopyMenu)}
              className="flex items-center px-1.5 py-1.5 rounded-r-lg border border-l-0 border-slate-200 bg-white text-slate-500 hover:bg-slate-50 transition-colors"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Dropdown menu */}
          {showCopyMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[180px] z-20">
              <button
                onClick={() => copyContent('markdown')}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Code className="w-4 h-4 text-slate-400" />
                Copiar Markdown
              </button>
              <button
                onClick={() => copyContent('plain')}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Type className="w-4 h-4 text-slate-400" />
                Copiar texto plano
              </button>
              <button
                onClick={() => copyContent('html')}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <FileCode className="w-4 h-4 text-slate-400" />
                Copiar HTML
              </button>
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  )
}
